/**
 * PII Scanning Hook for Document Upload
 * BRD-014: Client-Side Document Redaction
 *
 * This hook provides client-side PII detection before document upload.
 * Uses @myvault/pdf-viewer package for PDF processing and PII detection.
 *
 * Usage:
 *   const { scanFile, isScanning, scanResult } = usePIIScan();
 *
 *   // Scan a file when user drops it
 *   const result = await scanFile(file, { templateId: 'default' });
 *
 *   if (result.piiFound) {
 *     // Show preview modal for user to review/redact
 *   } else {
 *     // Proceed with normal upload
 *   }
 */

import { useState, useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * PII Match from scanning
 */
export interface PIIMatch {
  type: string;
  value: string;
  pageNumber: number;
  boundingBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  confidence: number;
  detector: string;
}

/**
 * PII Pattern definition
 */
export interface PIIPattern {
  name: string;
  label: string;
  pattern: RegExp;
  validate?: (match: string) => boolean;
  severity: 'high' | 'medium' | 'low';
}

export interface ScanOptions {
  templateId?: string;
  patterns?: PIIPattern[];
  maxPages?: number;
}

export interface PageScanResult {
  pageNumber: number;
  matches: PIIMatch[];
}

export interface ScanResult {
  file: File;
  piiFound: boolean;
  totalMatches: number;
  matchesByType: Record<string, number>;
  matchesBySeverity: { high: number; medium: number; low: number };
  pages: PageScanResult[];
  allMatches: PIIMatch[];
  scanDuration: number;
}

export interface PIIScanState {
  isScanning: boolean;
  progress: number;
  currentPage: number;
  totalPages: number;
  error: Error | null;
  scanResult: ScanResult | null;
}

// =============================================================================
// Dutch PII Patterns (inline for now - will use pdf-viewer package when ready)
// =============================================================================

/**
 * Validates a Dutch BSN (Burgerservicenummer) using the 11-check.
 */
function validateBSN(bsn: string): boolean {
  const cleaned = bsn.replace(/[\s-]/g, '');
  if (!/^\d{9}$/.test(cleaned)) return false;

  const digits = cleaned.split('').map(Number);
  const weights = [9, 8, 7, 6, 5, 4, 3, 2, -1];
  const sum = digits.reduce((acc, digit, idx) => acc + digit * weights[idx], 0);
  return sum % 11 === 0;
}

/**
 * Validates an IBAN using the mod-97 algorithm.
 */
function validateIBAN(iban: string): boolean {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(cleaned)) return false;
  if (cleaned.startsWith('NL') && cleaned.length !== 18) return false;

  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
  const numericString = rearranged.split('').map((char) => {
    const code = char.charCodeAt(0);
    if (code >= 65 && code <= 90) return (code - 55).toString();
    return char;
  }).join('');

  let remainder = 0;
  for (const digit of numericString) {
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
  }
  return remainder === 1;
}

const DUTCH_PII_PATTERNS: PIIPattern[] = [
  {
    name: 'BSN',
    label: 'Burgerservicenummer (BSN)',
    pattern: /\b\d{3}[\s.-]?\d{3}[\s.-]?\d{3}\b/g,
    validate: validateBSN,
    severity: 'high',
  },
  {
    name: 'IBAN',
    label: 'IBAN Bankrekeningnummer',
    pattern: /\bNL\d{2}\s?[A-Z]{4}\s?\d{4}\s?\d{4}\s?\d{2}\b/gi,
    validate: validateIBAN,
    severity: 'high',
  },
  {
    name: 'PHONE_MOBILE',
    label: 'Mobiel telefoonnummer',
    pattern: /\b(?:\+31\s?6|06)[\s.-]?\d{2}[\s.-]?\d{2}[\s.-]?\d{2}[\s.-]?\d{2}\b/g,
    severity: 'medium',
  },
  {
    name: 'EMAIL',
    label: 'E-mailadres',
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    severity: 'medium',
  },
  {
    name: 'POSTAL_CODE',
    label: 'Postcode',
    pattern: /\b[1-9]\d{3}\s?[A-Z]{2}\b/gi,
    severity: 'low',
  },
];

// =============================================================================
// Hook Implementation
// =============================================================================

export function usePIIScan() {
  const [state, setState] = useState<PIIScanState>({
    isScanning: false,
    progress: 0,
    currentPage: 0,
    totalPages: 0,
    error: null,
    scanResult: null,
  });

  /**
   * Scan a PDF file for PII
   * Note: This is a simplified implementation that reads PDF as text.
   * Full implementation would use PDF.js for proper text extraction with positions.
   */
  const scanFile = useCallback(
    async (file: File, options: ScanOptions = {}): Promise<ScanResult> => {
      const startTime = performance.now();
      const patterns = options.patterns || DUTCH_PII_PATTERNS;

      setState({
        isScanning: true,
        progress: 0,
        currentPage: 0,
        totalPages: 1,
        error: null,
        scanResult: null,
      });

      try {
        // Read file as text (simplified approach)
        // In production, this would use PDF.js for proper text extraction
        const arrayBuffer = await file.arrayBuffer();
        const textDecoder = new TextDecoder('utf-8', { fatal: false });
        const text = textDecoder.decode(arrayBuffer);

        setState((prev) => ({ ...prev, progress: 50 }));

        const allMatches: PIIMatch[] = [];

        // Run each pattern against the text
        for (const pattern of patterns) {
          pattern.pattern.lastIndex = 0;
          let match: RegExpExecArray | null;

          while ((match = pattern.pattern.exec(text)) !== null) {
            const matchedText = match[0];

            // Validate if validation function exists
            if (pattern.validate && !pattern.validate(matchedText)) {
              continue;
            }

            allMatches.push({
              type: pattern.name,
              value: matchedText,
              pageNumber: 1, // Simplified - would need PDF.js for actual page numbers
              boundingBox: {
                x: 0.1,
                y: 0.1 + (allMatches.length * 0.05),
                width: 0.3,
                height: 0.03,
              },
              confidence: pattern.validate ? 0.95 : 0.8,
              detector: pattern.name,
            });
          }
        }

        setState((prev) => ({ ...prev, progress: 100 }));

        // Calculate statistics
        const matchesByType: Record<string, number> = {};
        const matchesBySeverity = { high: 0, medium: 0, low: 0 };

        allMatches.forEach((match) => {
          matchesByType[match.type] = (matchesByType[match.type] || 0) + 1;
          const pattern = patterns.find((p) => p.name === match.type);
          if (pattern?.severity) {
            matchesBySeverity[pattern.severity]++;
          }
        });

        const result: ScanResult = {
          file,
          piiFound: allMatches.length > 0,
          totalMatches: allMatches.length,
          matchesByType,
          matchesBySeverity,
          pages: allMatches.length > 0 ? [{ pageNumber: 1, matches: allMatches }] : [],
          allMatches,
          scanDuration: performance.now() - startTime,
        };

        setState((prev) => ({
          ...prev,
          isScanning: false,
          scanResult: result,
        }));

        return result;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Scan failed');
        setState((prev) => ({
          ...prev,
          isScanning: false,
          error: err,
        }));
        throw err;
      }
    },
    []
  );

  /**
   * Reset scan state
   */
  const reset = useCallback(() => {
    setState({
      isScanning: false,
      progress: 0,
      currentPage: 0,
      totalPages: 0,
      error: null,
      scanResult: null,
    });
  }, []);

  return {
    scanFile,
    reset,
    isScanning: state.isScanning,
    progress: state.progress,
    currentPage: state.currentPage,
    totalPages: state.totalPages,
    error: state.error,
    scanResult: state.scanResult,
  };
}

/**
 * Get pattern severity for display
 */
export function getPatternSeverity(patternName: string): 'high' | 'medium' | 'low' {
  const pattern = DUTCH_PII_PATTERNS.find((p) => p.name === patternName);
  return pattern?.severity || 'medium';
}

/**
 * Get severity label for UI
 */
export function getSeverityLabel(severity: 'high' | 'medium' | 'low'): string {
  switch (severity) {
    case 'high':
      return 'Hoog risico';
    case 'medium':
      return 'Gemiddeld risico';
    case 'low':
      return 'Laag risico';
  }
}

/**
 * Get pattern label for UI
 */
export function getPatternLabel(patternName: string): string {
  const pattern = DUTCH_PII_PATTERNS.find((p) => p.name === patternName);
  return pattern?.label || patternName;
}

/**
 * Get severity color for UI
 */
export function getSeverityColor(severity: 'high' | 'medium' | 'low'): string {
  switch (severity) {
    case 'high':
      return '#dc2626'; // red-600
    case 'medium':
      return '#f59e0b'; // amber-500
    case 'low':
      return '#3b82f6'; // blue-500
    default:
      return '#6b7280'; // gray-500
  }
}
